import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, RotateCw, ScanFace, Trash2, UploadCloud, X, ZoomIn } from 'lucide-react';
import SearchableSelect from 'components/common/SearchableSelect';
import rankingComercialService from 'features/comercial/services/rankingComercialService';
import metasComerciaisService from 'features/comercial/services/metasComerciaisService';
import { EXPRESSOES_DE_PREVIA, desenharRosto } from 'features/comercial/cenas/cenasDoRanking';
import { corDoVendedor } from 'features/comercial/cenas/eventosDoRanking';
import {
  GIRO_FINO_MAXIMO,
  LADO_DA_FOTO,
  PONTOS_PADRAO,
  RECORTE_INICIAL,
  ZOOM_MAXIMO,
  ajustarRecorte,
  desenharRecorte,
  doQuadroParaAImagem,
  girarUmQuarto,
  giroQueNivelaOsOlhos,
  normalizarGiro,
  partesDoGiro,
  pontosNaImagem,
  pontosNoQuadro,
  pontosParaSalvar,
  recorteIntocado,
} from 'features/comercial/cenas/recorteDeFoto';

/**
 * Editor da foto do vendedor no Ranking.
 *
 * A foto vai para dois lugares: o avatar das linhas e as CENAS, onde a expressão é desenhada por
 * cima do rosto. Para a segunda ela precisa estar num padrão — quadrada, com o rosto enquadrado e
 * três pontos marcados (os dois olhos e a boca). Antes, o envio guardava o arquivo cru: foto
 * retangular entrava cortada pelo navegador, e as cenas não tinham como saber onde estava o rosto.
 *
 * Agora quem envia enquadra aqui mesmo: arrasta, dá zoom, gira (a volta inteira, porque foto de
 * celular chega deitada) e marca os três pontos, vendo ao
 * lado a mesma expressão que vai para a TV. O que sobe é a foto JÁ recortada, em 512 px — por isso
 * o arquivo de origem pode ser grande, e a foto do celular não precisa passar por outro programa.
 *
 * Reabrir uma foto já salva serve para remarcar os pontos (sem regravar a imagem) ou reenquadrar
 * um pouco. Para um enquadramento MAIS ABERTO é preciso escolher o arquivo de novo: o original não
 * fica guardado, só o recorte.
 */

const TAMANHO_MAXIMO_DO_ARQUIVO = 15 * 1024 * 1024;
const TIPOS_ACEITOS = ['image/jpeg', 'image/png', 'image/webp'];

const MARCAS = [
  { id: 'olho-esquerdo', rotulo: 'Olho à esquerda', sigla: 'E', cor: '#38BDF8', ler: (p) => p.olhos[0], gravar: (p, v) => ({ ...p, olhos: [v, p.olhos[1]] }) },
  { id: 'olho-direito', rotulo: 'Olho à direita', sigla: 'D', cor: '#38BDF8', ler: (p) => p.olhos[1], gravar: (p, v) => ({ ...p, olhos: [p.olhos[0], v] }) },
  { id: 'boca', rotulo: 'Boca', sigla: 'B', cor: '#F472B6', ler: (p) => p.boca, gravar: (p, v) => ({ ...p, boca: v }) },
];

const carregarElemento = (endereco) => new Promise((resolve, reject) => {
  const elemento = new Image();
  elemento.onload = () => resolve(elemento);
  elemento.onerror = () => reject(new Error('imagem ilegível'));
  elemento.src = endereco;
});

/**
 * Abre a foto já salva.
 *
 * Primeiro como blob: só assim o navegador deixa regravar a imagem depois de desenhá-la num canvas.
 * Se a origem da API não liberar a leitura, a foto ainda abre como imagem comum — dá para remarcar
 * os pontos, mas não para reenquadrar, e a tela diz isso.
 */
const abrirFotoSalva = async (endereco) => {
  try {
    const resposta = await fetch(endereco);
    if (!resposta.ok) throw new Error('foto indisponível');
    const local = URL.createObjectURL(await resposta.blob());

    return { elemento: await carregarElemento(local), local, regravavel: true };
  } catch (erro) {
    return { elemento: await carregarElemento(endereco), local: null, regravavel: false };
  }
};

const EditorDeFotoDoVendedor = ({ isOpen, vendedor, empresa, onClose, onSaved }) => {
  const [vendedores, setVendedores] = useState([]);
  const [selecionado, setSelecionado] = useState(null);
  const [carregandoVendedores, setCarregandoVendedores] = useState(false);
  // { elemento, imagem: { largura, altura }, nova, regravavel }
  const [fonte, setFonte] = useState(null);
  const [recorte, setRecorte] = useState(RECORTE_INICIAL);
  const [pontos, setPontos] = useState(PONTOS_PADRAO); // na IMAGEM, não no quadro
  const [expressao, setExpressao] = useState(0);
  const [abrindo, setAbrindo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const palco = useRef(null);
  const quadro = useRef(null);
  const previa = useRef(null);
  const arrasto = useRef(null);
  const enderecoLocal = useRef(null);

  const liberarEnderecoLocal = () => {
    if (enderecoLocal.current) URL.revokeObjectURL(enderecoLocal.current);
    enderecoLocal.current = null;
  };

  const adotar = useCallback((elemento, { nova, regravavel, local = null, pontosDoQuadro = PONTOS_PADRAO }) => {
    const imagem = { largura: elemento.naturalWidth, altura: elemento.naturalHeight };

    liberarEnderecoLocal();
    enderecoLocal.current = local;
    setFonte({ elemento, imagem, nova, regravavel });
    setRecorte(RECORTE_INICIAL);
    setPontos(pontosNaImagem(pontosDoQuadro, imagem, RECORTE_INICIAL));
  }, []);

  useEffect(() => {
    if (!isOpen || !vendedor) return undefined;

    let ativo = true;

    setSelecionado({ id: vendedor.id, label: vendedor.nome, nome: vendedor.nome });
    setFonte(null);
    setErro('');
    setExpressao(0);

    if (vendedor.foto_url) {
      setAbrindo(true);
      abrirFotoSalva(vendedor.foto_url)
        .then(({ elemento, local, regravavel }) => {
          if (!ativo) {
            if (local) URL.revokeObjectURL(local);
            return;
          }
          adotar(elemento, { nova: false, regravavel, local, pontosDoQuadro: vendedor.foto_pontos || PONTOS_PADRAO });
        })
        .catch(() => { if (ativo) setErro('Não foi possível abrir a foto atual. Escolha a imagem de novo.'); })
        .finally(() => { if (ativo) setAbrindo(false); });
    }

    setCarregandoVendedores(true);
    metasComerciaisService.listarVendedores(empresa)
      .then((resultado) => {
        if (!ativo) return;
        setVendedores((resultado.data || []).map((item) => ({ id: item.id, label: item.nome, nome: item.nome })));
      })
      .catch(() => { if (ativo) setVendedores([]); })
      .finally(() => { if (ativo) setCarregandoVendedores(false); });

    return () => {
      ativo = false;
      liberarEnderecoLocal();
    };
  }, [adotar, empresa, isOpen, vendedor]);

  const pontosDoQuadro = useMemo(
    () => (fonte ? pontosNoQuadro(pontos, fonte.imagem, recorte) : PONTOS_PADRAO),
    [fonte, pontos, recorte],
  );

  // O quadro é a foto que será salva: redesenha a cada ajuste.
  useEffect(() => {
    const contexto = quadro.current?.getContext?.('2d');
    if (!contexto || !fonte) return;

    desenharRecorte(contexto, fonte.elemento, fonte.imagem, recorte);
  }, [fonte, recorte]);

  // A prévia é o mesmo traço das cenas, sobre o quadro. Animada, porque olho de desenho parado não
  // mostra se pisca no lugar certo.
  const paraAPrevia = useRef({});
  paraAPrevia.current = { pontos: pontosDoQuadro, expressao, nome: selecionado?.nome || vendedor?.nome || '', id: selecionado?.id };

  useEffect(() => {
    if (!isOpen || !fonte) return undefined;

    let pedido = 0;
    const desenhar = (agora) => {
      if (previa.current && quadro.current) {
        const atual = paraAPrevia.current;
        desenharRosto(
          previa.current,
          { imagem: quadro.current, pontos: atual.pontos, nome: atual.nome, cor: corDoVendedor(atual.id) },
          EXPRESSOES_DE_PREVIA[atual.expressao][1],
          agora / 1000,
        );
      }
      pedido = requestAnimationFrame(desenhar);
    };
    pedido = requestAnimationFrame(desenhar);

    return () => cancelAnimationFrame(pedido);
  }, [isOpen, fonte]);

  if (!isOpen || !vendedor) return null;

  const podeReenquadrar = Boolean(fonte?.regravavel);
  const mudar = (parcial) => {
    if (!fonte || !podeReenquadrar) return;
    setRecorte((atual) => ajustarRecorte(fonte.imagem, { ...atual, ...parcial }));
  };

  const escolherArquivo = async (event) => {
    const arquivo = event.target.files?.[0];
    event.target.value = '';
    setErro('');
    if (!arquivo) return;

    if (!TIPOS_ACEITOS.includes(arquivo.type)) {
      setErro('Use uma imagem JPG, PNG ou WEBP.');
      return;
    }
    if (arquivo.size > TAMANHO_MAXIMO_DO_ARQUIVO) {
      setErro('A imagem deve ter no máximo 15 MB.');
      return;
    }

    const local = URL.createObjectURL(arquivo);
    try {
      adotar(await carregarElemento(local), { nova: true, regravavel: true, local });
    } catch (falha) {
      URL.revokeObjectURL(local);
      setErro('Não foi possível ler esta imagem. Tente outro arquivo.');
    }
  };

  const posicaoNoPalco = (event) => {
    const caixa = palco.current.getBoundingClientRect();
    return [(event.clientX - caixa.left) / caixa.width, (event.clientY - caixa.top) / caixa.height];
  };

  const comecarArrasto = (event, marca = null) => {
    if (!fonte || (!marca && !podeReenquadrar)) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    arrasto.current = { marca, de: posicaoNoPalco(event), recorte };
  };

  const arrastar = (event) => {
    const emCurso = arrasto.current;
    if (!emCurso || !fonte) return;

    const [x, y] = posicaoNoPalco(event);
    if (emCurso.marca) {
      const naImagem = doQuadroParaAImagem([x, y], fonte.imagem, recorte);
      setPontos((atuais) => emCurso.marca.gravar(atuais, naImagem));
      return;
    }

    setRecorte(ajustarRecorte(fonte.imagem, {
      ...emCurso.recorte,
      dx: emCurso.recorte.dx + (x - emCurso.de[0]),
      dy: emCurso.recorte.dy + (y - emCurso.de[1]),
    }));
  };

  const soltar = () => { arrasto.current = null; };

  // Pelo teclado, cada marca anda meio por cento do quadro por tecla; com Shift, dois.
  const moverPeloTeclado = (event, marca) => {
    const passos = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const passo = passos[event.key];
    if (!passo || !fonte) return;

    event.preventDefault();
    const tamanho = event.shiftKey ? 0.02 : 0.005;
    const [x, y] = marca.ler(pontosDoQuadro);
    const naImagem = doQuadroParaAImagem([x + passo[0] * tamanho, y + passo[1] * tamanho], fonte.imagem, recorte);
    setPontos((atuais) => marca.gravar(atuais, naImagem));
  };

  const nivelar = () => mudar({ giro: giroQueNivelaOsOlhos(pontosDoQuadro, recorte.giro) });
  // O controle mexe só no ajuste fino; os quartos de volta ficam com o botão ao lado.
  const giro = partesDoGiro(recorte.giro);

  const gerarArquivo = () => new Promise((resolve, reject) => {
    quadro.current.toBlob(
      (blob) => (blob ? resolve(new File([blob], 'rosto.jpg', { type: 'image/jpeg' })) : reject(new Error('recorte vazio'))),
      'image/jpeg',
      0.9,
    );
  });

  const salvar = async () => {
    if (!selecionado?.id || !fonte) {
      setErro('Selecione um vendedor e uma imagem para salvar.');
      return;
    }

    setSalvando(true);
    setErro('');

    try {
      // Só os pontos mudaram: a imagem fica como está no servidor, sem perder qualidade numa regravação.
      const regravar = fonte.nova || !recorteIntocado(recorte);
      const arquivo = regravar ? await gerarArquivo() : null;

      await rankingComercialService.salvarFotoVendedor(
        empresa,
        { id: selecionado.id, nome: selecionado.nome || selecionado.label },
        arquivo,
        pontosParaSalvar(pontosDoQuadro),
      );
      onSaved?.();
      onClose();
    } catch (falha) {
      setErro(falha.response?.data?.message || falha.message || 'Não foi possível salvar a foto.');
    } finally {
      setSalvando(false);
    }
  };

  const remover = async () => {
    if (!selecionado?.id) return;

    setSalvando(true);
    setErro('');

    try {
      await rankingComercialService.removerFotoVendedor(empresa, selecionado.id);
      onSaved?.();
      onClose();
    } catch (falha) {
      setErro(falha.response?.data?.message || falha.message || 'Não foi possível remover a foto.');
    } finally {
      setSalvando(false);
    }
  };

  const trocarVendedor = (opcao) => {
    setSelecionado(opcao);
    // A foto aberta é de quem estava selecionado; para outra pessoa, começa do zero.
    if (String(opcao?.id || '') !== String(selecionado?.id || '')) {
      liberarEnderecoLocal();
      setFonte(null);
    }
  };

  const rotuloDoControle = 'mb-1.5 flex items-center justify-between text-xs font-bold text-white/60';
  const classeDoControle = 'h-2 w-full cursor-pointer appearance-none rounded-full bg-white/[0.14] accent-[color:var(--accent)] disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="fixed top-0 right-0 bottom-0 left-0 z-[10000] flex items-center justify-center overflow-y-auto bg-slate-950/[0.78] p-4 text-white backdrop-blur-md">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Foto do vendedor"
        className="relative my-auto w-full max-w-4xl overflow-hidden rounded-[28px] border border-white/[0.12] bg-slate-950 shadow-[0_28px_100px_rgba(2,6,23,0.55)]"
      >
        <div className="pointer-events-none absolute top-0 right-0 bottom-0 left-0 bg-[radial-gradient(circle_at_12%_0%,color-mix(in_srgb,var(--accent)_26%,transparent),transparent_42%)]" />
        <div className="relative p-5 sm:p-6">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h3 className="text-2xl font-black">Foto do ranking</h3>
              <p className="mt-1 max-w-xl text-sm text-white/[0.6]">
                Enquadre o rosto e marque os olhos e a boca. É por esses três pontos que as cenas do ranking
                desenham a expressão em cima da foto.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/[0.08] text-white/70 transition hover:bg-white/[0.12] hover:text-white focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
              aria-label="Fechar o editor de foto"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid gap-6 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
            <div>
              <div
                ref={palco}
                className={`relative mx-auto aspect-square w-full max-w-[360px] touch-none select-none overflow-hidden rounded-[28px] bg-white/[0.06] ring-1 ring-white/[0.16] ${fonte && podeReenquadrar ? 'cursor-grab active:cursor-grabbing' : ''}`}
                onPointerDown={(event) => comecarArrasto(event)}
                onPointerMove={arrastar}
                onPointerUp={soltar}
                onPointerCancel={soltar}
              >
                <canvas
                  ref={quadro}
                  width={LADO_DA_FOTO}
                  height={LADO_DA_FOTO}
                  className={`h-full w-full ${fonte ? '' : 'invisible'}`}
                  aria-label="Recorte da foto"
                />
                {fonte ? (
                  <>
                    {/* O círculo é o que aparece nas cenas; os cantos só aparecem no avatar das linhas. */}
                    <div className="pointer-events-none absolute top-0 right-0 bottom-0 left-0 rounded-full ring-2 ring-white/70 [box-shadow:0_0_0_999px_rgba(2,6,23,0.42)]" />
                    {MARCAS.map((marca) => {
                      const [x, y] = marca.ler(pontosDoQuadro);

                      return (
                        <button
                          key={marca.id}
                          type="button"
                          aria-label={`${marca.rotulo}: arraste ou use as setas para posicionar`}
                          title={marca.rotulo}
                          onPointerDown={(event) => comecarArrasto(event, marca)}
                          onPointerMove={arrastar}
                          onPointerUp={soltar}
                          onPointerCancel={soltar}
                          onKeyDown={(event) => moverPeloTeclado(event, marca)}
                          className="absolute z-10 grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 cursor-move touch-none place-items-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                          style={{ left: `${Math.max(0, Math.min(1, x)) * 100}%`, top: `${Math.max(0, Math.min(1, y)) * 100}%` }}
                        >
                          <span
                            className="grid h-6 w-6 place-items-center rounded-full border-2 border-slate-950 text-[10px] font-black text-slate-950 shadow-[0_2px_10px_rgba(2,6,23,0.6)]"
                            style={{ backgroundColor: marca.cor }}
                          >
                            {marca.sigla}
                          </span>
                        </button>
                      );
                    })}
                  </>
                ) : (
                  <div className="absolute top-0 right-0 bottom-0 left-0 grid place-items-center px-6 text-center text-sm text-white/[0.55]">
                    {abrindo ? 'Abrindo a foto atual…' : 'Escolha uma imagem para começar.'}
                  </div>
                )}
              </div>

              <div className="mx-auto mt-4 max-w-[360px] space-y-3">
                <label className="block">
                  <span className={rotuloDoControle}>
                    <span className="flex items-center gap-1.5"><ZoomIn className="h-3.5 w-3.5" aria-hidden="true" /> Zoom</span>
                    <span className="tabular-nums text-white/45">{recorte.zoom.toFixed(1).replace('.', ',')}x</span>
                  </span>
                  <input
                    type="range"
                    min="1"
                    max={ZOOM_MAXIMO}
                    step="0.01"
                    value={recorte.zoom}
                    disabled={!podeReenquadrar}
                    onChange={(event) => mudar({ zoom: Number(event.target.value) })}
                    className={classeDoControle}
                  />
                </label>
                <div className="flex items-end gap-2">
                  <label className="block min-w-0 flex-1">
                    <span className={rotuloDoControle}>
                      <span>Giro</span>
                      <span className="tabular-nums text-white/45">{Math.round(recorte.giro)}°</span>
                    </span>
                    <input
                      type="range"
                      min={-GIRO_FINO_MAXIMO}
                      max={GIRO_FINO_MAXIMO}
                      step="0.5"
                      value={giro.fino}
                      disabled={!podeReenquadrar}
                      onChange={(event) => mudar({ giro: normalizarGiro(giro.quartos * 90 + Number(event.target.value)) })}
                      className={classeDoControle}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => mudar({ giro: girarUmQuarto(recorte.giro) })}
                    disabled={!podeReenquadrar}
                    title="Gira a foto um quarto de volta. Use quando ela vier deitada ou de ponta-cabeça."
                    className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.055] px-3 text-sm font-bold text-white/85 transition hover:bg-white/[0.1] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <RotateCw className="h-4 w-4" aria-hidden="true" />
                    Girar 90°
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={nivelar}
                    disabled={!podeReenquadrar}
                    title="Gira a foto até os dois olhos marcados ficarem na mesma altura"
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.055] px-3 text-sm font-bold text-white/85 transition hover:bg-white/[0.1] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ScanFace className="h-4 w-4" aria-hidden="true" />
                    Nivelar olhos
                  </button>
                  <label className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-white/[0.2] bg-white/[0.055] px-3 text-sm font-bold text-white/85 transition focus-within:ring-2 focus-within:ring-[color:var(--accent)] hover:border-[color:var(--accent)] hover:bg-white/[0.1]">
                    <UploadCloud className="h-4 w-4 text-[color:var(--accent)]" aria-hidden="true" />
                    {fonte ? 'Trocar imagem' : 'Escolher imagem'}
                    <input type="file" accept={TIPOS_ACEITOS.join(',')} onChange={escolherArquivo} className="sr-only" />
                  </label>
                </div>
                {fonte && !podeReenquadrar && (
                  <p className="text-xs text-[color:var(--status-warn)]">
                    Esta foto só permite reposicionar os pontos. Para mudar o enquadramento, escolha a imagem de novo.
                  </p>
                )}
              </div>
            </div>

            <div className="flex min-w-0 flex-col gap-4">
              <div>
                <span className="mb-2 block text-xs font-bold text-white/60">Vendedor</span>
                <SearchableSelect
                  options={vendedores}
                  value={selecionado}
                  onChange={trocarVendedor}
                  placeholder="Buscar vendedor"
                  loading={carregandoVendedores}
                  dropdownClassName="max-h-56"
                />
              </div>

              <div className="rounded-2xl border border-white/[0.1] bg-white/[0.04] p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-bold text-white/60">Como fica na cena</span>
                  <div role="group" aria-label="Expressão da prévia" className="flex flex-wrap gap-1.5">
                    {EXPRESSOES_DE_PREVIA.map(([nome], indice) => (
                      <button
                        key={nome}
                        type="button"
                        aria-pressed={expressao === indice}
                        onClick={() => setExpressao(indice)}
                        className={`h-8 rounded-full px-3 text-xs font-bold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] ${expressao === indice ? 'bg-white text-slate-950' : 'bg-white/[0.08] text-white/70 hover:bg-white/[0.14] hover:text-white'}`}
                      >
                        {nome}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-3 grid items-center gap-4 sm:grid-cols-[200px_minmax(0,1fr)]">
                  <canvas
                    ref={previa}
                    width={400}
                    height={400}
                    className={`mx-auto h-[200px] w-[200px] ${fonte ? '' : 'opacity-0'}`}
                    aria-label="Prévia da expressão sobre a foto"
                  />
                  <ol className="list-decimal space-y-1.5 pl-4 text-sm text-white/[0.68]">
                    <li>Arraste a foto e ajuste o zoom até o rosto ocupar o círculo.</li>
                    <li>Leve <strong className="text-white">E</strong> e <strong className="text-white">D</strong> para o centro de cada olho.</li>
                    <li>Leve <strong className="text-white">B</strong> para o meio da boca, entre os lábios.</li>
                    <li>Confira na prévia se os olhos de desenho caíram no lugar.</li>
                  </ol>
                </div>
              </div>

              <p className="text-xs text-white/70">
                Use a foto só com a autorização da pessoa. Ela aparece para todos no ranking de {empresa} e nas
                cenas da TV, onde ganha expressões de desenho — inclusive de susto, raiva e choro quando é
                ultrapassada.
              </p>

              {erro && (
                <p role="alert" className="rounded-xl border border-rose-400/[0.20] bg-rose-400/[0.10] px-3 py-2 text-sm text-rose-200">
                  {erro}
                </p>
              )}

              <div className="mt-auto grid gap-3 sm:grid-cols-[auto_1fr]">
                <button
                  type="button"
                  onClick={remover}
                  disabled={salvando || !selecionado?.id}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.055] px-4 text-sm font-bold text-rose-200 transition hover:bg-rose-400/[0.10] focus:outline-none focus:ring-2 focus:ring-rose-300 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  Remover foto
                </button>
                <button
                  type="button"
                  onClick={salvar}
                  disabled={salvando || !fonte || !selecionado?.id}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[color:var(--accent)] px-4 text-sm font-black text-white transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ImagePlus className="h-4 w-4" aria-hidden="true" />
                  {salvando ? 'Salvando…' : 'Salvar foto'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditorDeFotoDoVendedor;
